import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useSelector } from "../../common/hooks/useSelector";
import { compositeProduct, CompositeStep } from "./compositorEngine";
import type { CompositeLayer, GlobalHighlight } from "./compositorEngine";
import {
  selectCompositeLayers,
  selectBackCompositeLayers,
} from "../../common/slices/specsEditor/selectors";

export type {
  CompositeLayer,
  GlobalHighlight,
  CompositeStep,
} from "./compositorEngine";

export interface CompositeOutput {
  url: string | null;
  steps: CompositeStep[];
}

export interface CompositePreviewState {
  front: CompositeOutput;
  back: CompositeOutput;
  showBack: boolean;
}

export function useCompositeOutput(
  layers: CompositeLayer[],
  globalHighlight: GlobalHighlight | null
): CompositeOutput {
  return useCompositeEngine(layers, globalHighlight);
}

/**
 * Composites the front view by reading directly from Redux state via selectors.
 */
export function useFrontComposite(): CompositeOutput {
  const layers = useSelector(selectCompositeLayers);
  return useCompositeOutput(layers, null);
}

/**
 * Composites the back view by reading directly from Redux state via selectors.
 */
export function useBackComposite(): CompositeOutput {
  const layers = useSelector(selectBackCompositeLayers);
  const hasBack = layers.some((l) => l.maskUrl || l.highlightUrl);
  return useCompositeOutput(hasBack ? layers : [], null);
}

/**
 * Core engine hook — takes compositor inputs and returns a data URL + debug steps.
 * Debounces to avoid rapid re-renders during color changes.
 */
function useCompositeEngine(
  layers: CompositeLayer[],
  globalHighlight: GlobalHighlight | null
): CompositeOutput {
  const [output, setOutput] = useState<CompositeOutput>({
    url: null,
    steps: [],
  });
  const seqRef = useRef(0);

  // Serialize inputs to a stable string for dependency comparison
  const inputKey = JSON.stringify({ layers, globalHighlight });

  const debouncedComposite = useMemo(
    () =>
      debounce(
        async (
          seq: number,
          ls: CompositeLayer[],
          gh: GlobalHighlight | null
        ) => {
          const result = await compositeProduct(ls, gh);
          if (seq === seqRef.current) {
            setOutput({
              url: result?.dataUrl ?? null,
              steps: result?.steps ?? [],
            });
          }
        },
        80
      ),
    []
  );

  useEffect(() => {
    const seq = ++seqRef.current;

    const validLayers = layers.filter((l) => l.maskUrl || l.highlightUrl);
    if (validLayers.length === 0) {
      setOutput({ url: null, steps: [] });
      return;
    }

    debouncedComposite(seq, layers, globalHighlight);

    return () => debouncedComposite.cancel();
  }, [inputKey, debouncedComposite]); // eslint-disable-line react-hooks/exhaustive-deps

  return output;
}
